import { Pipe, PipeTransform } from '@angular/core';
import { ActivityDto } from '@bo-client/shared/models/activityDto';
import { StatusCatalogKey } from '@bo-client/shared/status/status.model';
import { StatusService } from '@bo-client/shared/status/status.service';
import { TenantlessEmailPipe } from '@bo-client/shared/pipes/tenantless-email.pipe';

@Pipe({
  name: 'activityName'
})
export class ActivityTypePipe implements PipeTransform {
  transform(value: any, args?: any): any {
    switch (value) {
      case 'CONVERSATION':
        return 'Chat';
      case 'PHONE_CALL':
        return 'Telefonanruf';
      case 'EMAIL_ACTIVITY':
        return 'EMail gesendet';
    }
    return value;
  }
}

@Pipe({
  name: 'lastActivityState'
})
export class LastActivityStatePipe implements PipeTransform {
  transform(listAct: any[], args?: any): any {
    if (listAct == null) {
      return '';
    }
    const numItems = listAct.length;
    if (numItems == 0) {
      return '';
    }
    if (listAct[numItems - 1].state == null) {
      return '';
    }
    switch (listAct[numItems - 1].state) {
      case 'OPEN':
        return 'Geöffnet';
      case 'IN_PROGRESS':
        return 'In Bearbeitung';
      case 'DONE':
        return 'Erledigt';
    }
    return '';
  }
}

@Pipe({
  name: 'activityIcon'
})
export class ActivityIconPipe implements PipeTransform {
  transform(value: any, hasStatusTo?: any, hasStatusFrom?: any): any {
    switch (value) {
      case 'CONVERSATION':
        return 'forum';
      case 'PHONE_CALL':
        return 'android_dialer';
      case 'EMAIL_ACTIVITY':
        return 'mail';
      case 'AUDIT_ACTIVITY':
        if (hasStatusTo) {
          if (hasStatusFrom) {
            return 'change_circle';
          } else {
            return 'arrow_circle_right';
          }
        } else {
          return 'settings';
        }
    }
    return 'pending';
  }
}

@Pipe({
  name: 'activityDescription'
})
export class ActivityDescriptionPipe implements PipeTransform {
  transform(value: any, activity?: ActivityDto): any {
    switch (value) {
      case 'CONVERSATION':
        return 'Chat';
      case 'PHONE_CALL':
        return 'Telefon';
      case 'EMAIL_ACTIVITY':
        return 'E-Mail';
      case 'AUDIT_ACTIVITY':
        if (activity.stateTo) {
          if (activity.stateFrom) {
            return 'Statuswechsel';
          } else {
            return 'durchgeführte Aktion';
          }
        } else {
          return 'Audit';
        }
    }
    return '----';
  }
}

/** Kataloge für Aktivitäten ohne bekanntes Modul, in dieser Reihenfolge (erster Treffer). */
const ACTIVITY_STATUS_KEYS: readonly StatusCatalogKey[] = [
  'installerPassList',
  'commissioning',
  'gridRegistration',
  'feederContract'
];

/** Statuswechsel einer Audit-Aktivität als „alt -> neu (Benutzer)“; unbekannte Werte erscheinen roh. */
@Pipe({
  name: 'activityInformation',
  pure: false
})
export class ActivityInformationPipe implements PipeTransform {
  constructor(private statusService: StatusService, private tenantlessEmailPipe: TenantlessEmailPipe) {}

  transform(activity?: ActivityDto, module?: StatusCatalogKey): any {
    switch (activity.type) {
      case 'EMAIL_ACTIVITY':
        return activity.subject;
      case 'AUDIT_ACTIVITY':
        return (
          this.stateLabel(activity.stateFrom, module) +
          ' -> ' +
          this.stateLabel(activity.stateTo, module) +
          ' (' +
          (activity.createdBy == 'anonymousUser'
            ? 'externer Benutzer'
            : this.tenantlessEmailPipe.transform(activity.createdBy)) +
          ')'
        );
    }
    return '----';
  }

  /**
   * Anzeigename eines Status aus den Statuskatalogen. Status ohne Treffer (z. B. `DELETED` aus dem
   * Lösch-Audit, ohne Modul auch Status von Bestellung und Anschlussänderung) erscheinen im Rohwert, ein
   * fehlender Status als `—`.
   */
  private stateLabel(state: string | undefined, module?: StatusCatalogKey): string {
    if (!state) {
      return '—';
    }
    const option = module
      ? this.statusService.find(module, state, true)
      : this.statusService.findAcross(state, ACTIVITY_STATUS_KEYS, true);
    return option?.displayName ?? state;
  }
}

@Pipe({
  name: 'userActivity'
})
export class UserActivityPipe implements PipeTransform {
  transform(userName?: string): any {
    if (userName == 'anonymousUser') {
      return 'externe Benutzer';
    }
    return userName;
  }
}

/** Kataloge für den Aktivitätsdialog ohne bekanntes Modul, in dieser Reihenfolge (erster Treffer). */
const AUDIT_DIALOG_STATUS_KEYS: readonly StatusCatalogKey[] = [
  'commissioning',
  'gridRegistration',
  'feederContract',
  'installerPassList'
];

@Pipe({
  name: 'statusEntityAuditActivity',
  pure: false
})
export class StatusEntityAuditActivityPipe implements PipeTransform {
  constructor(private statusService: StatusService) {}

  transform(statusName?: any, module?: StatusCatalogKey): any {
    if (statusName == null) {
      return '---';
    }
    const currentStatus = module
      ? this.statusService.find(module, statusName, true)
      : this.statusService.findAcross(statusName, AUDIT_DIALOG_STATUS_KEYS, true);
    if (currentStatus && currentStatus.displayName) {
      return currentStatus.displayName;
    }
    return statusName;
  }
}
